import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { money, statusLabel, statusClass, Pill, shortDate } from "@/lib/money";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { AlertCircle, CheckCircle2 } from "lucide-react";

export default function MyLoans() {
  const [loans, setLoans] = useState([]);
  const [payments, setPayments] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/me/loans");
        setLoans(r.data);
        const entries = await Promise.all(
          r.data.map(async (l) => {
            try {
              const p = await api.get(`/loans/${l.application_id}/payments`);
              return [l.application_id, p.data];
            } catch { return [l.application_id, []]; }
          })
        );
        setPayments(Object.fromEntries(entries));
      } catch (e) {
        toast.error(formatApiError(e));
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return <Layout><div className="p-8 text-slate-500">Loading your loans…</div></Layout>;
  }

  if (loans.length === 0) {
    return (
      <Layout>
        <div className="fade-in">
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>My loans</h1>
          <Card className="rounded-xl border-slate-200 mt-4"><CardContent className="p-10 text-center">
            <p className="font-medium">You have no active loans</p>
            <p className="text-sm text-slate-500 mt-1">
              Once an application is approved and the funds are released, your balance and
              payment schedule appear here.
            </p>
          </CardContent></Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="fade-in space-y-5">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>My loans</h1>
          <p className="text-sm text-slate-500">Your balance, what is due next, and everything you have paid.</p>
        </div>

        {loans.map((l) => {
          const overdue = l.days_past_due > 0;
          const pays = (payments[l.application_id] || []).filter((p) => p.status === "posted");
          return (
            <Card key={l.id} className="rounded-xl border-slate-200 overflow-hidden">
              <CardContent className="p-0">
                <div className="p-5 border-b border-slate-100">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <h2 className="text-lg font-semibold">{l.product_name}</h2>
                      <p className="text-sm text-slate-500">Opened {shortDate(l.opened_at)}</p>
                    </div>
                    <Pill className={statusClass(l.status)}>{statusLabel(l.status)}</Pill>
                  </div>

                  {/* The single most important number on the page. */}
                  <div className="mt-4">
                    <div className="text-[11px] uppercase tracking-widest text-slate-500">Balance remaining</div>
                    <div className="text-3xl font-bold tabular-nums" style={{ fontFamily: "Outfit" }}>
                      {money(l.outstanding)}
                    </div>
                    <div className="text-sm text-slate-500 mt-1">
                      {money(l.total_paid)} paid of {money(l.total_charged)}
                    </div>
                    <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full bg-emerald-600 transition-all"
                        style={{ width: `${l.total_charged ? Math.min(100, (l.total_paid / l.total_charged) * 100) : 0}%` }}
                      />
                    </div>
                  </div>

                  {overdue ? (
                    <div className="mt-4 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3">
                      <AlertCircle className="h-5 w-5 text-red-700 shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <div className="font-semibold text-red-800">
                          {money(l.arrears)} overdue — {l.days_past_due} day{l.days_past_due === 1 ? "" : "s"} past due
                        </div>
                        <div className="text-red-700">
                          Late fees may continue to be added. Please contact us to arrange payment.
                        </div>
                      </div>
                    </div>
                  ) : l.status === "closed" ? (
                    <div className="mt-4 flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm">
                      <CheckCircle2 className="h-5 w-5 text-emerald-700 shrink-0" />
                      <span className="font-semibold text-emerald-800">This loan is fully repaid. Thank you.</span>
                    </div>
                  ) : l.next_due_date ? (
                    <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 p-3">
                      <div className="text-[11px] uppercase tracking-widest text-slate-500">Next payment</div>
                      <div className="flex items-baseline justify-between mt-0.5">
                        <span className="text-lg font-semibold tabular-nums">{money(l.next_due_amount)}</span>
                        <span className="text-sm text-slate-600">due {shortDate(l.next_due_date)}</span>
                      </div>
                    </div>
                  ) : null}
                </div>

                <Tabs defaultValue="schedule">
                  <div className="px-5 pt-4">
                    <TabsList>
                      <TabsTrigger value="schedule" data-testid="my-tab-schedule">Schedule</TabsTrigger>
                      <TabsTrigger value="payments" data-testid="my-tab-payments">Payments ({pays.length})</TabsTrigger>
                    </TabsList>
                  </div>

                  <TabsContent value="schedule" className="mt-0">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                          <tr>
                            <th className="text-left px-5 py-2">#</th>
                            <th className="text-left px-4 py-2">Due</th>
                            <th className="text-right px-4 py-2 hidden sm:table-cell">Amount</th>
                            <th className="text-right px-4 py-2">Remaining</th>
                            <th className="text-left px-5 py-2">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {l.schedule.map((i) => (
                            <tr key={i.installment} className="border-t border-slate-100">
                              <td className="px-5 py-2 text-slate-500">{i.installment}</td>
                              <td className="px-4 py-2 whitespace-nowrap">{shortDate(i.due_date)}</td>
                              <td className="px-4 py-2 text-right tabular-nums hidden sm:table-cell">{money(i.amount)}</td>
                              <td className="px-4 py-2 text-right tabular-nums font-medium">{money(i.balance)}</td>
                              <td className="px-5 py-2"><Pill className={statusClass(i.status)}>{statusLabel(i.status)}</Pill></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </TabsContent>

                  <TabsContent value="payments" className="mt-0">
                    {pays.length === 0 ? (
                      <div className="p-8 text-center text-sm text-slate-500">
                        No payments recorded on this loan yet.
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                            <tr>
                              <th className="text-left px-5 py-2">Receipt</th>
                              <th className="text-left px-4 py-2">Date</th>
                              <th className="text-left px-4 py-2">Method</th>
                              <th className="text-right px-5 py-2">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pays.map((p) => (
                              <tr key={p.id} className="border-t border-slate-100">
                                <td className="px-5 py-2 font-mono text-xs">{p.receipt_no}</td>
                                <td className="px-4 py-2 whitespace-nowrap">{shortDate(p.received_at)}</td>
                                <td className="px-4 py-2 text-slate-600">{statusLabel(p.method)}</td>
                                <td className="px-5 py-2 text-right tabular-nums font-medium">{money(p.amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </Layout>
  );
}
